import memoizeOne from 'memoize-one';
import React, { Component, ReactNode } from 'react';
import { Product, ReferencePrice } from '~/Api/User/ProductGroup/CzMaxPrice';
import parseHumanFloat from '~/utils/Number/parseHumanFloat';
import { calculateProductPackagePrice } from '~/Product';

import { TailwindTooltip } from '~/libs/ui/atoms/TailwindTooltip/TailwindTooltip';
import { CountrySourceWithTooltip } from '~/libs/ui/molecules/CountrySourceWithTooltip';

import { RiFormula } from 'react-icons/ri';
import { FaExclamationCircle } from 'react-icons/fa';
import { CompilanceIconWithTooltip } from '~/libs/ui/molecules/CompilanceIconWithTooltip';
import { Link } from '~/libs/ui/atoms/Link';
import { PriceSelect } from '~/libs/ui/atoms/PriceSelect';
import { Checkbox } from '~/libs/ui/atoms/Checkbox';
import { Input } from '~/libs/ui/atoms/Input';

export interface ReferencePriceValue {
	value: string;
	parentValue: null;
	prompt: React.ReactNode;
}

export interface Values {
	referencePrice: {
		value: ReferencePriceValue | null;
		isValid: boolean;
	};
	packageSizeForSingleUnit: {
		value: string;
		isValid: boolean | null;
	};
	packageSize: {
		value: string;
		isValid: boolean | null;
	};
	selected: boolean;
}
export type OnChangeListener = (
	product: Product,
	referencePrice: ReferencePriceValue | null,
	packageSize: string,
	packageSizeForSingleUnit: string
) => void;

export type OnSelectListener = (product: Product, selected: boolean, range: boolean) => void;

interface Props {
	czMaxPriceProduct: Product;
	values: Values;
	referencePackageSize: number | null;
	disableSelection: boolean;
	highlightProduct: boolean;
	lastSelectedProduct: boolean;
	detailLink(product: { id: string }): { pathname: string; search: string };
	style: any;
	lowestPriceIndex: number | null;
	onChange: OnChangeListener;
	onSelect: OnSelectListener;
	onSave(): void;
	checkRow(value: boolean): void;
	checked: boolean;
}

export default class Row extends Component<Props> {
	static defaultProps = {
		referencePackageSize: null,
		disableSelection: false,
		highlightProduct: false,
		lastSelectedProduct: false,
		style: null,
	};

	resolveReferencePrice = memoizeOne((type: string | null, czMaxPriceProduct: Product): ReferencePrice | null => {
		if (type === null) {
			return null;
		}
		return (
			czMaxPriceProduct.referencePrices.find((referencePrice) => referencePrice.calculatedPrice.type === type) ?? null
		);
	});

	shouldComponentUpdate(nextProps: Readonly<Props>) {
		return (
			JSON.stringify(this.props.czMaxPriceProduct) !== JSON.stringify(nextProps.czMaxPriceProduct) ||
			this.props.values.packageSize.isValid !== nextProps.values.packageSize.isValid ||
			this.props.values.packageSize.value !== nextProps.values.packageSize.value ||
			this.props.values.packageSizeForSingleUnit.isValid !== nextProps.values.packageSizeForSingleUnit.isValid ||
			this.props.values.packageSizeForSingleUnit.value !== nextProps.values.packageSizeForSingleUnit.value ||
			this.props.values.referencePrice.isValid !== nextProps.values.referencePrice.isValid ||
			this.props.values.referencePrice.value?.value !== nextProps.values.referencePrice.value?.value ||
			this.props.values.selected !== nextProps.values.selected ||
			this.props.referencePackageSize !== nextProps.referencePackageSize ||
			this.props.disableSelection !== nextProps.disableSelection ||
			this.props.highlightProduct !== nextProps.highlightProduct ||
			this.props.lastSelectedProduct !== nextProps.lastSelectedProduct ||
			this.props.style !== nextProps.style ||
			this.props.lowestPriceIndex !== nextProps.lowestPriceIndex ||
			this.props.onChange !== nextProps.onChange ||
			this.props.onSelect !== nextProps.onSelect ||
			this.props.checked !== nextProps.checked
		);
	}

	render() {
		const {
			czMaxPriceProduct,
			values,
			referencePackageSize,
			disableSelection,
			highlightProduct,
			lastSelectedProduct,
			detailLink,
			style,
			lowestPriceIndex,
			onChange,
			onSave,
			checked,
			checkRow,
		} = this.props;
		const { product } = czMaxPriceProduct;
		const detailLinkString = detailLink(product);
		const referencePrice = this.resolveReferencePrice(values.referencePrice.value?.value ?? null, czMaxPriceProduct);
		const prompt = referencePrice !== null ? this.renderReferencePricePrompt(referencePrice) : null;

		const onSelectPrice = (value: string, reactNode: ReactNode) => {
			const optionValue = {
				value,
				parentValue: null,
				prompt: reactNode,
			};
			onChange(czMaxPriceProduct, optionValue, values.packageSize.value, values.packageSizeForSingleUnit.value);
		};

		return (
			<div
				className={`product-row ${highlightProduct ? 'product-row-highlight' : ''} ${
					product.status.isRemoved() || product.status.isBeforeIntroduction() ? 'product-row-removed' : ''
				}`}
				data-testid='row'
				style={style}
			>
				<div
					className='relative inline-flex h-full w-9 items-center justify-center grow-0 shrink-0'
					data-testid='delete-product'
				>
					<Checkbox id={`product-select-${product.id}`} checked={checked} onChange={checkRow} />
				</div>
				<div className='pl-1 w-20 grow-0 shrink-0'>
					<Link styles='inline-flex items-center justify-center w-full h-full' tabIndex={-1} to={detailLinkString}>
						<CountrySourceWithTooltip
							countryAlpha={product.dataset.source.country.alpha2}
							countryName={product.dataset.source.country.name}
							dataSourceShortName={product.dataset.source.shortName}
							dataSourceDescription={product.dataset.source.description}
						/>
					</Link>
				</div>
				<div className='pl-3 flex-1'>
					<Link
						styles='flex flex-col justify-center w-full h-full'
						tabIndex={-1}
						to={detailLinkString}
						title={product.uppercasedBrandName ? product.uppercasedBrandName : ''}
					>
						<span className='product-row-text'>{product.uppercasedBrandName}</span>
					</Link>
				</div>
				<div className='pl-3 flex-1'>
					<Link
						styles='inline-flex flex-col justify-center w-full h-full'
						tabIndex={-1}
						to={detailLinkString}
						title={product.uppercasedMarketingAuthorizationHolder ? product.uppercasedMarketingAuthorizationHolder : ''}
					>
						<span className='product-row-text'>{product.uppercasedMarketingAuthorizationHolder}</span>
					</Link>
				</div>
				<div className='pl-3 flex-1'>
					<Link
						styles='inline-flex flex-col justify-center w-full h-full'
						tabIndex={-1}
						to={detailLinkString}
						title={product.uppercasedDosageForm ? product.uppercasedDosageForm : ''}
					>
						<span className='product-row-text'>{product.uppercasedDosageForm}</span>
					</Link>
				</div>
				<div className='pl-3 flex-1'>
					<Link
						styles='inline-flex flex-col justify-center w-full h-full'
						tabIndex={-1}
						to={detailLinkString}
						title={product.dosageStrength ? product.dosageStrength : ''}
					>
						<span className='product-row-text'>{product.dosageStrength}</span>
					</Link>
				</div>
				<div className='pl-3 flex-1'>
					{czMaxPriceProduct.singleUnitPackageSize ? (
						<label htmlFor={`product-package-${product.id}`} className='w-full max-w-14 h-full flex items-center'>
							<Input
								id={`product-package-${product.id}`}
								name={`product-package-${product.id}`}
								value={values.packageSizeForSingleUnit.value}
								isInvalid={values.packageSizeForSingleUnit.isValid === false}
								onBlur={onSave}
								onChange={(value) =>
									onChange(czMaxPriceProduct, values.referencePrice.value, values.packageSize.value, value)
								}
							/>
						</label>
					) : (
						<Link
							styles='inline-flex flex-col justify-center w-full h-full'
							tabIndex={-1}
							to={detailLinkString}
							title={product.packageSize ? product.packageSize : ''}
						>
							<span className='product-row-text'>{product.packageSize}</span>
						</Link>
					)}
				</div>
				<div className='pl-3 pr-5 w-40 relative grow-0 shrink-0'>
					{czMaxPriceProduct.referencePrices.length > 1 ? (
						<div className='inline-flex flex-col justify-center w-full h-full'>
							{referencePrice && (
								<PriceSelect
									referencePrice={referencePrice}
									referencePrices={czMaxPriceProduct.referencePrices}
									onSelect={onSelectPrice}
								/>
							)}
						</div>
					) : (
						<div className='inline-flex flex-col justify-center w-full h-full'>
							<abbr
								className='product-row-price product-row-price-tooltip'
								data-testid='cell-reference-price'
								title={referencePrice ? referencePrice.calculatedPrice.description : ''}
							>
								{referencePrice && (
									<span className='absolute left-full top-0 -ml-5 -mt-0.5 w-3 h-3 leading-3 rounded-full text-[11px] font-semibold bg-white text-center'>
										?
									</span>
								)}
								{prompt}
							</abbr>
						</div>
					)}
					{czMaxPriceProduct.singleUnitPackageSize && (
						<TailwindTooltip
							reference={<FaExclamationCircle className='w-4 h-4 text-red-600' />}
							displayClass='!absolute top-1/2 right-0 -mt-2 inline-block h-4 align-top'
							position='left'
						>
							Single unit package size
						</TailwindTooltip>
					)}
				</div>
				<div className='pl-2 w-16 grow-0 shrink-0'>
					<label
						htmlFor={`product-package-for-calc-${product.id}`}
						className='flex items-center w-full h-full cursor-text'
					>
						<Input
							id={`product-package-for-calc-${product.id}`}
							name={`product-package-for-calc-${product.id}`}
							value={values.packageSize.value}
							placeholder={`${referencePackageSize ?? ''}`}
							isInvalid={values.packageSize.isValid === false}
							onBlur={onSave}
							onChange={(value) =>
								onChange(czMaxPriceProduct, values.referencePrice.value, value, values.packageSizeForSingleUnit.value)
							}
						/>
					</label>
				</div>
				<div className='pl-3 w-32 grow-0 shrink-0'>
					<Link
						data-testid='calculated-price'
						styles='inline-flex flex-col justify-center w-full h-full'
						tabIndex={-1}
						to={detailLinkString}
					>
						<div className='product-row-price'>{this.renderPackagePrice(referencePrice)}</div>
					</Link>
				</div>
				<div className='w-16 grow-0 shrink-0 text-right'>
					<div className='relative flex justify-end items-center h-full w-full grow-0 shrink-0'>
						<CompilanceIconWithTooltip
							compliance={czMaxPriceProduct.productCompliance.resolution}
							title={czMaxPriceProduct.productCompliance.resolveRuleDescription()}
							decisionComment={czMaxPriceProduct.productCompliance.decisionComment}
							referenceDataSourceVersion={czMaxPriceProduct.productCompliance.czechReferenceDataSources.version}
							referenceDataSourceSince={czMaxPriceProduct.productCompliance.czechReferenceDataSources.effectiveSince}
						/>
						<div className='ml-1'>
							<Checkbox
								id={`product-selection-${product.id}`}
								checked={!disableSelection && values.selected}
								disabled={disableSelection}
								focused={lastSelectedProduct}
								onChange={this.selectionChanged}
							/>
						</div>
					</div>
				</div>
				<div className='w-9 flex grow-0 shrink-0'>
					<Link styles='inline-flex items-center justify-center w-full h-full' tabIndex={-1} to={detailLinkString}>
						{lowestPriceIndex !== null ? (
							<TailwindTooltip
								reference={<RiFormula className='inline-block mt-1 w-3 h-3 align-top' />}
								displayClass='relative inline-block w-5 h-5 rounded-full text-black bg-gray-200 text-center align-midle'
								position='left'
							>
								<strong className='block mb-0.5'>Lowest price:</strong>
								{this.renderLowerPriceIndex(lowestPriceIndex)} ex-factory price
							</TailwindTooltip>
						) : null}
					</Link>
				</div>
			</div>
		);
	}

	renderReferencePricePrompt(price: ReferencePrice): ReactNode {
		return (
			<>
				<div>{price.calculatedPrice.price.roundedHumanFormat()}</div>
				<strong className='block leading-normal'>{price.sourcePrice.price?.roundedHumanFormat()}</strong>
			</>
		);
	}

	renderPackagePrice(referencePrice: ReferencePrice | null): ReactNode {
		if (referencePrice === null) {
			return null;
		}

		const {
			values: { packageSize },
			referencePackageSize,
		} = this.props;
		if (
			packageSize.isValid === false ||
			(packageSize.isValid === null && packageSize.value !== '') ||
			referencePackageSize === null
		) {
			return null;
		}

		const parsedPackageSize = packageSize.value !== '' ? parseHumanFloat(packageSize.value) : referencePackageSize;
		if (parsedPackageSize === null) {
			return null;
		}

		const { calculatedPrice, sourcePrice } = referencePrice;

		return (
			<div data-testid='cell-calc-price'>
				{calculateProductPackagePrice(
					calculatedPrice.price,
					parsedPackageSize,
					referencePackageSize
				).roundedHumanFormat()}
				<strong className='product-row-price-local'>
					{sourcePrice.price !== null
						? calculateProductPackagePrice(
								sourcePrice.price,
								parsedPackageSize,
								referencePackageSize
						  ).roundedHumanFormat()
						: null}
				</strong>
			</div>
		);
	}

	selectionChanged = (checked: boolean, event: React.MouseEvent<HTMLLabelElement, MouseEvent>) => {
		const { czMaxPriceProduct, onSelect } = this.props;
		onSelect(czMaxPriceProduct, checked, event.nativeEvent.shiftKey);
	};

	private renderLowerPriceIndex(index: number) {
		switch (index) {
			case 0:
				return 'Lowest';
			case 1:
				return '2nd lowest';
			case 2:
				return '3rd lowest';
			default:
				return `${index}. lowest`;
		}
	}
}
